import React from 'react';
import Head from 'react-helmet';
import { connect } from 'react-redux';
import capitalize from 'lodash/capitalize';

interface AreaCard {
  id?: string;
  name: string;
  uriV2: string;
  listingCount: number;
  resourceId?: string;
}

export interface CityPageStateProps {
  cityName: string;
  stateName: string;
  sitemap: Record<string, Record<string, Array<AreaCard>>>;
  path: string;
  isInitialSsrPage: boolean;
  areaCityPageLoading: boolean;
  srpUrl: string | null;
}

export interface CityPageDispatchProps {
  dispatch: (action: any) => any;
}

type CityPageProps = CityPageStateProps & CityPageDispatchProps;

interface ReduxState {
  area: { citySitemap: any };
  location: { current: { pathname: string } };
  app: { loadCityPageComplete: boolean; isInitialSsrPage: boolean };
}

import Title from 'app/shared/modules/Title';
import Linker from 'app/shared/modules/Linker';
import TabGroup from 'app/shared/modules/TabGroup';
import TabItem from 'app/shared/modules/TabItem';
import Center from 'app/shared/modules/Center';
import Container from 'app/shared/core/Container';
import AreaCardList from 'app/shared/modules/state-page/AreaCardList';
import AreaActions from 'app/shared/flux/actions/AreaActions';
import ErrorActions from 'app/shared/flux/actions/ErrorActions';
import { cityPageStyles, cityPageTabsStyles, cityPageTabStyles } from './CityPage.styles';
import SkeletonStatePage from 'app/shared/modules/skeleton/SkeletonStatePage';
import HpBreadcrumb from 'app/shared/modules/HpBreadcrumb';
import pathsManifest from 'app/shared/utils/pathsManifest';
import { generateCitySrpUrl, parseCitySitemapPath } from 'app/shared/utils/cityUtils';
import NotFoundPage from 'app/shared/pages/NotFoundPage';
import { registerClientProfilerPage } from 'app/shared/utils/clientProfiler';

const titleCapitalize = (word: string): string => {
  const start = word[0];
  if (start === start.toUpperCase()) {
    return word;
  } else {
    return capitalize(word);
  }
};

class CityPage extends React.Component<CityPageProps> {
  constructor(props: CityPageProps) {
    super(props);
  }

  componentDidMount() {
    registerClientProfilerPage('hotpads-city-page');
    const { dispatch, path, isInitialSsrPage } = this.props;
    if (isInitialSsrPage) {
      return false;
    }

    const cityData = parseCitySitemapPath(path);
    if (cityData) {
      const { citySlug, stateSlug } = cityData;
      dispatch(AreaActions.loadCityPage({ stateSlug, citySlug }));
    }
  }

  serverRouteWillMount(reactContext: any) {
    const path = reactContext.state.path;
    const cityData = parseCitySitemapPath(path);

    if (cityData) {
      const { citySlug, stateSlug } = cityData;
      const { dispatch } = reactContext.store;
      return dispatch(AreaActions.loadCityPage({ stateSlug, citySlug })).catch((error: any) => {
        dispatch(
          ErrorActions.errorHandler({
            error,
            errorLocation: 'component.cityPage.serverRouteWillMount',
            errorClass: 'areaActions',
          }),
        );
      });
    }
  }

  render() {
    const { areaCityPageLoading, sitemap, cityName, stateName, path, srpUrl } = this.props;
    const cityData = parseCitySitemapPath(path);

    if (!cityData) {
      // Invalid city/state combination - return 404 page
      return <NotFoundPage />;
    }

    const { stateSlug } = cityData;

    const breadcrumbs = [
      {
        name: 'Sitemap',
        uri: pathsManifest.SitemapPage,
      },
      {
        name: stateName,
        uri: `/${stateSlug}`,
      },
      {
        name: cityName,
        uri: '',
      },
    ];

    if (areaCityPageLoading) {
      return (
        <div className={cityPageStyles}>
          <SkeletonStatePage />
        </div>
      );
    }

    const fullCityName = `${cityName}, ${stateName}`;

    return (
      <div className={cityPageStyles}>
        <Head
          title={`${fullCityName} rentals | HotPads`}
          meta={[
            {
              name: 'description',
              content:
                `Easily search for apartments, houses, condos, affordable housing and for-rent-by-owner ` +
                `properties in ${fullCityName} by ` +
                `searching your desired neighborhood, county or zip code area with the largest and most ` +
                `trusted rental site.`,
            },
          ]}
        />
        <Container>
          <HpBreadcrumb breadcrumbs={breadcrumbs} />
          <Center>
            <Title size="xl" htmlTag="h1">
              {srpUrl ? <Linker to={srpUrl}>{fullCityName} rentals</Linker> : `${fullCityName} rentals`}
            </Title>
          </Center>
          {/* @ts-ignore */}
          <TabGroup loadAllContent customClass={cityPageTabsStyles}>
            {Object.keys(sitemap).map((key) => {
              const propertyTypeTitle = titleCapitalize(key);
              return (
                <TabItem key={key} tabTitle={propertyTypeTitle} className={cityPageTabStyles}>
                  {Object.keys(sitemap[key]).map((geo) => {
                    return <AreaCardList key={geo} title={`Popular ${geo}`} areas={sitemap[key][geo]} />;
                  })}
                </TabItem>
              );
            })}
          </TabGroup>
        </Container>
      </div>
    );
  }
}

const mapStateToProps = (state: ReduxState): CityPageStateProps => {
  const {
    area: { citySitemap = {} },
  } = state;

  const cityData = parseCitySitemapPath(state.location.current.pathname);
  const srpUrl = cityData ? generateCitySrpUrl(cityData.citySlug, cityData.stateAbbreviation) : null;

  return {
    areaCityPageLoading: !state.app.loadCityPageComplete,
    path: state.location.current.pathname,
    isInitialSsrPage: state.app.isInitialSsrPage,
    cityName: cityData?.cityName || '',
    stateName: cityData?.stateName || '',
    sitemap: citySitemap,
    srpUrl,
  };
};

export default connect(mapStateToProps)(CityPage);
